// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TabProps } from "@insite/mobius/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { xBaseTheme } from "@keller/XDL/types";
import { color, styledScrollbar, xScreen } from "@keller/XDL/Utilities";
import { css } from "styled-components";

const tabSelectedStyle = css`
    outline-width: 0;
    border-bottom: 0;
    background: ${color("common.white")};
    cursor: auto;
    &:after {
        content: " ";
        width: calc(100% + 1px);
        height: 1px;
        background: ${color("common.white")};
        position: absolute;
        left: -0.5px;
        right: -0.5px;
        bottom: 0;
        @media print {
            height: 2px;
            bottom: -2px;
        }
    }
    &:before {
        display: none;
    }

    border-color: ${color("common.ui11")} ${color("common.ui5")} transparent;
    & + li:before {
        display: none;
    }

    &:hover {
        color: ${color("text.main")};
    }
`;

const tabStyle = css`
    padding: 14px 50px;
    min-width: initial;
    cursor: pointer;
    user-select: none;
    position: relative;
    border-width: 5px 1px 0;
    border-style: solid;
    border-color: transparent;
    transition: all 0.2s;
    &:before {
        content: " ";
        height: 50%;
        width: 1px;
        background: ${color("common.ui5")};
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
    }

    &:hover {
        border-bottom: 0;
        color: ${color("common.ui1")};
    }

    &:focus {
        ${tabSelectedStyle}
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            size: 18,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        css: css`
            ${tabStyle}
            ${({ selected }: TabProps) => selected && tabSelectedStyle}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                align-items: flex-start;
                &:before {
                    content: " ";
                    width: 100%;
                    position: absolute;
                    bottom: 0;
                    height: 1px;
                    background: ${color("common.ui5")};
                }

                & > li:first-child:before {
                    display: none;
                }

                ${styledScrollbar(4)}
            `,
            tabContent: css`
                margin-top: 0;
                ${xScreen("gt", "md")} {
                    border-width: 0 1px 1px;
                    border-style: solid;
                    border-color: transparent ${color("common.ui5")} ${color("common.ui5")};
                    background: ${color("common.white")};
                    box-shadow: ${getProp("theme.shadows.4")};
                    padding: 16px 12px 25px;
                }

                ${xScreen("lte", "md")} {
                    padding: 0;
                }

                @media print {
                    padding: 0;
                    margin-bottom: 20px;
                    display: block;

                    & > ${TypographyStyle} {
                        display: inline-block;
                        ${tabStyle}
                        ${tabSelectedStyle}
                    }

                    & > div {
                        padding: 16px 12px 25px;
                        border: 1px solid ${color("common.border")};
                    }
                }
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
